import {
  Avatar,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { LuX } from "react-icons/lu";
import { useSearchParams } from "react-router";
import CDS from "design-system";
import { differenceInCalendarDays, format, isThisWeek } from "date-fns";
import { useTaskDetailQuery, useMarkCompleteTask, useUpdateTask } from "features/Resources/Task/hooks";
import { isInPast } from "helpers/time";
import { ShowRichText } from "components/FormElements/Fields/RichTextAreaField";
import { Task } from "features/Resources/Task/models";
import TaskActionButtons from "features/Resources/Task/Components/TaskActionButtons";

type TaskState = "active" | "overdue" | "complete" | "skipped";

function getTaskState(task: Task): TaskState {
  if (task.status === "completed") return "complete";
  if (task.status === "skipped") return "skipped";
  if (task.isOverdue) return "overdue";
  return "active";
}

const PILL_STYLES: Record<TaskState, { bg: string; color: string }> = {
  active: { bg: "var(--chakra-colors-gray-100)", color: "var(--chakra-colors-gray-600)" },
  overdue: { bg: "#FAEEDA", color: "#BA7517" },
  complete: { bg: "#E1F5EE", color: "#0F6E56" },
  skipped: { bg: "var(--chakra-colors-gray-100)", color: "var(--chakra-colors-gray-600)" },
};

function pillLabel(state: TaskState, task: Task): string {
  if (state === "overdue") {
    const days = task.dueOn ? differenceInCalendarDays(new Date(), new Date(task.dueOn)) : 0;
    return `Overdue · ${days}d`;
  }
  if (state === "complete") return "✓ Complete";
  if (state === "skipped") return "Skipped";
  return "Active";
}

function dateLabel(state: TaskState, task: Task): string | null {
  if (state === "complete" || state === "skipped") {
    if (!task.statusUpdatedAt) return null;
    const prefix = state === "complete" ? "Done" : "Skipped";
    return `${prefix} ${format(new Date(task.statusUpdatedAt), "MMM d")}`;
  }
  if (!task.dueOn) return null;
  const due = new Date(task.dueOn);
  if (state === "overdue") return `Was due ${format(due, "MMM d")}`;
  if (isThisWeek(due, { weekStartsOn: 1 })) return `Due ${format(due, "EEEE")}`;
  return `Due ${format(due, "MMM d")}`;
}

interface AssigneeTaskDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string | null;
}

export default function AssigneeTaskDrawer({ isOpen, onClose, taskId }: AssigneeTaskDrawerProps) {
  const [, setSearchParams] = useSearchParams();
  const { data: task, isLoading } = useTaskDetailQuery(taskId || "", { enabled: !!taskId });
  const markComplete = useMarkCompleteTask(task);
  const { mutate: updateTask } = useUpdateTask(task?.id);

  const handleSkip = useCallback(() => {
    if (!task) return;
    const effectiveStartDate = task.startedAt || task.startAt;
    const reopenStatus = isInPast(effectiveStartDate) ? "active" : "upcoming";
    updateTask({ status: task.status === "skipped" ? reopenStatus : "skipped" });
  }, [task, updateTask]);

  const handleActionClick = useCallback(() => {
    if (!task) return;
    setSearchParams({ task_action: task.id });
  }, [task, setSearchParams]);

  if (!taskId) return null;

  const state = task ? getTaskState(task) : "active";
  const pill = PILL_STYLES[state];
  const dl = task ? dateLabel(state, task) : null;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader pb={0} pt={5} px={6} borderBottomWidth={0} position="relative">
          <CDS.IconButton
            aria-label="Close"
            customIcon={<LuX size={18} />}
            variant="ghost"
            size="sm"
            position="absolute"
            top={3}
            right={3}
            onClick={onClose}
          />
          <VStack align="start" spacing={2} pr={8}>
            <Text fontSize="xl" fontWeight="500" color="content.primary" lineHeight="1.3">
              {isLoading ? "Loading..." : task?.name}
            </Text>
            {task && (
              <HStack spacing={3} align="center">
                <Box
                  display="inline-flex"
                  alignItems="center"
                  px={2.5}
                  py={0.5}
                  borderRadius="full"
                  bg={pill.bg}
                  fontSize="xs"
                  fontWeight="600"
                  color={pill.color}
                >
                  {pillLabel(state, task)}
                </Box>
                {dl && (
                  <Text fontSize="xs" color="content.tertiary">{dl}</Text>
                )}
              </HStack>
            )}
          </VStack>
          <Divider mt={4} />
        </DrawerHeader>

        <DrawerBody px={6} py={5}>
          {task && (
            <Box mb={5}>
              <TaskActionButtons
                task={task}
                onMarkComplete={markComplete}
                onSkip={handleSkip}
                onActionClick={handleActionClick}
              />
            </Box>
          )}

          {task?.assignee && (
            <Flex gap={2} flexWrap="wrap" align="center" mb={5}>
              <CDS.BasePill cursor="default" _hover={{}} alignItems="center" gap={2}>
                <Avatar
                  name={task.assignee.fullName}
                  src={task.assignee.avatarUrl ?? undefined}
                  size="xs"
                  bg="cdsIndigo.300"
                  color="white"
                  flexShrink={0}
                />
                <Flex direction="column" gap="0">
                  <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
                    Assignee
                  </Text>
                  <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3">
                    {task.assignee.fullName}
                  </Text>
                </Flex>
              </CDS.BasePill>
            </Flex>
          )}

          {isLoading && (
            <Text fontSize="sm" color="content.tertiary">
              Loading...
            </Text>
          )}
          {!isLoading && !task && (
            <Text fontSize="sm" color="content.tertiary">
              Task not found
            </Text>
          )}
          {!isLoading && task && (
            <ShowRichText
              label="Description"
              richText={task.descriptionDisplay ?? task.description ?? ""}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
